/* eslint-disable no-underscore-dangle */
import React, { useRef, useEffect, useState } from 'react';
import { objectOf, shape } from 'prop-types';
import useStore from 'state/state';
import settings from 'settings';
import GTMTracker from 'tracking/gtm-tracker';
import loadPcModules from '../../loaders/loadPCModules';
import ApplicationControl from './ApplicationControl';
import EventBridge from './EventBridge';

import LoadingScreen from '../LoadingScreen/LoadingScreen';
import styles from './Application.module.scss';

const promisify = (fun, args, ctx) =>
  new Promise((resolve, reject) => {
    args.push((err, data) => {
      if (err) {
        reject(err);
      }
      resolve(data);
    });
    fun.apply(ctx, args);
  });

const Application = ({ config, data }) => {
  const { pc } = window;
  const appData = useStore((state) => state.appData);
  const setIsPcLoaded = useStore((state) => state.setIsPcLoaded);
  const setMode = useStore((state) => state.setMode);
  const canvasRef = useRef(null);
  const application = useRef(null);
  const applicationControl = useRef(null);
  const eventBridge = useRef(null);
  const [loadingVisible, setLoadingVisible] = useState(true);
  const setProductObject = useStore((state) => state.setProductObject);
  const productObject = useStore((state) => state.productObject);
  const setIsIntro = useStore((state) => state.setIsIntro);
  const setProductData = useStore((state) => state.setProductData);
  const isMapOpen = useStore((state) => state.isMapOpen);
  const isOverviewMapOpen = useStore((state) => state.isOverviewMapOpen);
  const setIsMapOpen = useStore((state) => state.setIsMapOpen);
  const setIsOverviewMapOpen = useStore((state) => state.setIsOverviewMapOpen);
  const isShareOpen = useStore((state) => state.isShareOpen);
  const isOnboardingOpen = useStore((state) => state.isOnboardingOpen);
  const isWhitePaperOpen = useStore((state) => state.isWhitePaperOpen);
  const isArticleOpen = useStore((state) => state.isArticleOpen);
  const isContactOpen = useStore((state) => state.isContactOpen);
  const isGalleryOpen = useStore((state) => state.isGalleryOpen);
  const setRoomId = useStore((state) => state.setRoomId);
  const isAboutOpen = useStore((state) => state.isAboutOpen);
  const glueUrl = `${settings.getBasePath()}files/assets/57946583/1/basis.wasm.js`;
  const wasmUrl = `${settings.getBasePath()}files/assets/57946585/1/basis.wasm.wasm`;
  const fallbackUrl = `${settings.getBasePath()}files/assets/57946584/1/basis.js`;

  pc.basisSetDownloadConfig(glueUrl, wasmUrl, fallbackUrl);
  // new conf for loading wasm.
  pc.WasmModule.setConfig('DracoDecoderModule', {
    glueUrl: `${settings.getBasePath()}files/assets/129776713/1/draco.wasm.js`,
    wasmUrl: `${settings.getBasePath()}files/assets/129776714/1/draco.wasm.wasm`,
    fallbackUrl: `${settings.getBasePath()}files/assets/129776712/1/draco.js`,
  });

  const onResize = (app, canvas) => {
    if (!canvas) {
      return;
    }

    if (!app) {
      return;
    }

    const { parentElement } = canvas;
    if (!parentElement) {
      return;
    }

    app.setCanvasFillMode(
      pc.FILLMODE_NONE,
      parentElement.offsetWidth,
      parentElement.offsetHeight
    );
  };

  const [progressPercent, setProgressPercent] = useState(0);
  const [preloadEnd, setPreloadEnd] = useState(false);

  // Prelaoding of application.
  const onPreloadProgress = (progress) => {
    const formattedProgress = progress.toFixed(2);
    setProgressPercent(formattedProgress);
  };

  const onPreloadEnd = () => {};

  const removeLoading = () => {
    setLoadingVisible(false);

    setTimeout(() => {
      setIsIntro(false);
    }, 1500);

    if (applicationControl.current) {
      const { hash } = window.location;
      let location = hash.split('?')[0];

      if (location) {
        location = location.replace('#', '');
      }

      applicationControl.current.setData(appData);
      applicationControl.current.updateTextPlates();
      applicationControl.current.manualInit(location);
    }
  };

  useEffect(() => {
    if (applicationControl.current) {
      if (!productObject) {
        applicationControl.current.closeProductView();
      }
    }
  }, [productObject]);

  useEffect(() => {
    if (applicationControl.current) {
      if (isMapOpen) {
        applicationControl.current.openMap();
      }
    }
  }, [isMapOpen]);

  useEffect(() => {
    if (applicationControl.current) {
      if (isOverviewMapOpen) {
        applicationControl.current.openOverviewMap();
      }
    }
  }, [isOverviewMapOpen]);

  useEffect(() => {
    if (
      isShareOpen ||
      isOnboardingOpen ||
      isWhitePaperOpen ||
      isArticleOpen ||
      isContactOpen ||
      isGalleryOpen ||
      isAboutOpen
    ) {
      if (application.current) {
        applicationControl.current.lockLookControls();
      }
    } else if (application.current) {
      applicationControl.current.unlockLookControls();
    }
  }, [
    isArticleOpen,
    isContactOpen,
    isGalleryOpen,
    isOnboardingOpen,
    isShareOpen,
    isWhitePaperOpen,
    isAboutOpen,
  ]);

  function isTouchDevice() {
    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      navigator.msMaxTouchPoints > 0
    );
  }

  useEffect(() => {
    let canvas = canvasRef.current;
    let mounted = true;

    if (!pc) {
      return;
    }

    if (!canvas) {
      return;
    }

    let app = null;

    // WASM modules loaded continue setting upp playcanvas.
    // console.log('load modules');
    const loadModulesComplete = () => {
      // console.log('load modules complete');
      const devices = {
        elementInput: new pc.ElementInput(canvas),
        keyboard: new pc.Keyboard(canvas),
        mouse: new pc.Mouse(canvas),
        gamepads: new pc.GamePads(),
        touch: isTouchDevice() ? new pc.TouchDevice(canvas) : null,
      };

      const options = {
        elementInput: devices.elementInput,
        keyboard: devices.keyboard,
        mouse: devices.mouse,
        gamepads: devices.gamepads,
        touch: devices.touch,
        graphicsDeviceOptions: config.contextOptions,
        assetPrefix: config.assetPrefix || '',
        scriptPrefix: config.scriptPrefix || '',
        scriptsOrder: config.scripts || [],
      };

      app = new pc.Application(canvas, options);
      application.current = app;
      app.autoRender = true;

      app.on('preload:progress', (event) => {
        if (mounted) {
          // Remove some % because we are not fully done!
          onPreloadProgress(event - event * 0.095);
        }
      });
      app.on('preload:end', (event) => {
        if (mounted) {
          onPreloadEnd(event);
        }
      });
      app.on('data-event:data-loaded', () => {
        if (mounted) {
          // onDataInit(app, data);
        }
      });
      app.on('start', () => {
        app.scene.root.script.app.setInvertedMouseLook(false);

        if (mounted) {
          onResize(app, canvas);
        }
      });

      const promise = async () => {
        await promisify(app.configure, [config.configFilename], app);
        await promisify(app.preload, [], app);
        await promisify(app.loadScene, [config.scenePath], app);
      };

      applicationControl.current = ApplicationControl.init({
        application: app,
      });

      eventBridge.current = EventBridge.init({
        application: app,
        startCallback: () => {
          if (applicationControl.current) {
            applicationControl.current.unlockLookControls();
          }
        },

        onProductOpen: (id, type) => {
          setProductObject(null);
          setProductData(null);
          setProductObject({
            id,
            type: parseInt(type, 10),
          });
          GTMTracker.push('HotspotViewOpen', id);
        },

        onReturnToFloor: () => {
          setIsMapOpen(false);
          setIsOverviewMapOpen(false);
        },

        onRoomDidUpdate: (roomData) => {
          setRoomId(roomData.id);
        },

        onCloseProduct: () => {
          setProductData(null);
          setProductObject(null);
        },

        onRoomSelected: (room) => {
          let roomName = '';
          if (room === 0) {
            roomName = appData.mainData.rooms.roomATitle;
          }
          if (room === 1) {
            roomName = appData.mainData.rooms.roomBTitle;
          }
          if (room === 2) {
            roomName = appData.mainData.rooms.roomCTitle;
          }
          if (room === 3) {
            roomName = appData.mainData.rooms.roomDTitle;
          }
          if (room === 4) {
            roomName = appData.mainData.rooms.roomETitle;
          }
          GTMTracker.push('OveviewRoomSelected', roomName);
        },

        onModeChange: () => {
          if (applicationControl.current) {
            const mode = applicationControl.current.getMode();
            setMode(mode);
          }
        },
      });

      promise().then(() => {
        if (mounted) {
          setIsPcLoaded(true);
          setProgressPercent(1);
          const to = setTimeout(() => {
            clearTimeout(to);
            setPreloadEnd(true);
            app.start();
          }, 100);
        }
      });

      window.addEventListener('resize', () => {
        if (mounted) {
          onResize(app, canvas);
        }
      });
      window.addEventListener('orientationchange', () => {
        if (mounted) {
          onResize(app, canvas);
        }
      });

      [
        'fullscreenchange',
        'webkitfullscreenchange',
        'mozfullscreenchange',
        'msfullscreenchange',
      ].forEach(
        (eventType) =>
          document.addEventListener(eventType, () => {
            if (mounted) {
              onResize(app, canvas);
            }
          }),
        false
      );

      // Will handle the broadcast events from playcanvas.
      applicationControl.current = ApplicationControl.init({
        application: app,
      });
    };

    // Load WASM files for ammo.js
    loadPcModules.load(loadModulesComplete, false);

    return () => {
      // setIntroComplete(false);
      // setGlobalPreloadEnd(false);
      app.destroy();
      mounted = false;
      if (!canvasRef.current) {
        return;
      }
      canvas = canvasRef.current;

      if (application.current) {
        try {
          application.current.destroy();
          applicationControl.current.destroy();
          application.current = null;
          eventBridge.current.destroy();
          // console.log('Application destroyed');
        } catch (error) {
          // console.log('failed to destroy ', error);
        }
      }

      canvasRef.current = null;
      window.removeEventListener('resize', null);
      window.removeEventListener('orientationchange', null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className={styles.canvas}>
      <canvas className={styles.canvas} id="game" ref={canvasRef} />
      {loadingVisible && (
        <LoadingScreen
          progressPercent={Number(progressPercent)}
          preloadEnd={preloadEnd}
          onComplete={removeLoading}
          data={data}
        />
      )}
    </div>
  );
};

Application.propTypes = {
  config: shape().isRequired,
  data: objectOf(shape).isRequired,
};

export default Application;
